import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// Models
import { ConsolidacionFinancieraViewModel } from "src/app/_core/models/centros/cebe-contract-list-base.model";

// Services
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

@Component({
    selector: "app-consolidacion-financiera-autocomplete",
    templateUrl: "./consolidacion-financiera-autocomplete.component.html",
    styleUrls: ["./consolidacion-financiera-autocomplete.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ConsolidacionFinancieraAutocompleteComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class ConsolidacionFinancieraAutocompleteComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() label = "Consolidación Financiera";
	@Input() placeholder =
		"Buscar consolidación financiera por código o descripción";
	@Input() required = false;
	@Input() disabled = false;
	@Input() hint =
		"Busque por código o descripción de consolidación financiera";

	// Outputs
	@Output() consolidacionFinancieraSelected =
		new EventEmitter<ConsolidacionFinancieraViewModel>();

	// State
	consolidacionesFinancieras: ConsolidacionFinancieraViewModel[] = [];
	consolidacionFinancieraId: number | null = null;
	selectedConsolidacionFinanciera: ConsolidacionFinancieraViewModel | null =
		null;

	// ControlValueAccessor
	private onChange = (_value: number | null): void => {
		// This is intentional
	};
	private onTouched = (): void => {
		// This is intentional
	};

	constructor(
		private readonly masterService: MasterService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.loadConsolidacionesFinancieras();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: number | null): void {
		this.consolidacionFinancieraId = value ?? null;
		this.syncSelected();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	/**
	 * Carga la lista de consolidaciones financieras
	 */
	private loadConsolidacionesFinancieras(): void {
		this.masterService
			.getConsolidacionFinancieraList()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (consolidacionesFinancieras) => {
					this.consolidacionesFinancieras =
						consolidacionesFinancieras;
					this.syncSelected();
					this.cdr.markForCheck();
				},
				error: () => {
					this.notificationService.showError(
						"Error al cargar las consolidaciones financieras",
					);
				},
			});
	}

	/** Recalcula el objeto seleccionado a partir del id actual. */
	private syncSelected(): void {
		this.selectedConsolidacionFinanciera =
			this.consolidacionFinancieraId != null
				? (this.consolidacionesFinancieras.find(
						(c) => c.id === this.consolidacionFinancieraId,
					) ?? null)
				: null;
	}

	/**
	 * Obtiene el texto mostrado para una consolidación financiera
	 */
	displayConsolidacionFinanciera = (
		consolidacionFinanciera: ConsolidacionFinancieraViewModel | string,
	): string => {
		if (typeof consolidacionFinanciera === "string") {
			return consolidacionFinanciera;
		}
		return consolidacionFinanciera && consolidacionFinanciera.descripcion
			? consolidacionFinanciera.descripcion
			: "";
	};

	/** Selección desde el autocomplete `.ds`. */
	onValueChange(
		consolidacionFinanciera: ConsolidacionFinancieraViewModel | null,
	): void {
		this.consolidacionFinancieraId = consolidacionFinanciera
			? consolidacionFinanciera.id
			: null;
		this.selectedConsolidacionFinanciera = consolidacionFinanciera;
		this.onChange(this.consolidacionFinancieraId);
		this.onTouched();
		if (consolidacionFinanciera) {
			this.consolidacionFinancieraSelected.emit(consolidacionFinanciera);
		}
		this.cdr.markForCheck();
	}

	onControlTouched(): void {
		this.onTouched();
	}
}
